import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Alert, Snackbar } from "@mui/material";

import LeadStageColumn from "./LeadStageColumn";
import LeadModal from "./LeadModal";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import LeadQuickView from "../popup";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import ContactQuickView from "../../../../mainAccounts/accounts/contact/popup";

export default function LeadBoard({
  filters,
  groupBy = "special",
  sendFilters,
  ProductList = [],
  PackageList = [],
  DealStages = [],
  ClientList = [],
  BrandList = [],
  UserList = [],
  country = [],
  onLoadMoreClients,
  hasMoreClients,
  isLoadingClients,
  SubProductList = [],
  fetchSubProducts = () => {},
  fetchPackages = () => {},
}) {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [isApiPending, setIsApiPending] = useState(false);
  const [alertData, setAlertData] = useState({
    isShow: false,
    type: "success",
    message: "",
  });
  const [counts, setCounts] = useState({});
  const [cardLoad, setCardload] = useState(false);

  const divRef = useRef(null);
  const isApiPendingRef = useRef(false);
  const apiCall = useApiCall();

  const handleCloseAlert = () =>
    setAlertData({ isShow: false, type: "", message: "" });

  useEffect(() => {
    // reset state when groupBy changes
    setIsLoading(true);
    setLeads([]);
    setStages([]);
    setPagination({});
    loadLeadsAndStages(1);
  }, [filters, groupBy]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setIsLoading(true);
    loadLeadsAndStages(1);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const loadLeadsAndStages = async (page = "") => {
    // build query with filters (same format as list)
    let extraUrl = `?group_by=${groupBy}&page=${page}`;
    if (filters?.length) {
      for (let i = 0; i < filters.length; i++) {
        // skip empty values
        if (
          filters[i]?.value !== undefined &&
          filters[i]?.value !== null &&
          filters[i]?.value !== ""
        ) {
          extraUrl += `&${filters[i].name}=${filters[i].value}`;
        }
      }
    }
    setIsApiPending(true);
    try {
      // use constructed extraUrl
      const res = await apiCall({
        method: "get",
        url: `leads/leads-kanban${extraUrl}`,
      });

      sendFilters(res?.custom_filters);
      setCounts(res.counts);

      if (res.status && res.data) {
        const data = res.data.data || res.data;

        if (page === 1) {
          // fresh load
          const dynamicStages = Object.keys(data).map((key) => ({
            id: key,
            name: key.replace(/_/g, " "),
          }));
          setStages(dynamicStages);
        } else {
          // append new stages if not already added
          const newStages = Object.keys(data).map((key) => ({
            id: key,
            name: key.replace(/_/g, " "),
          }));

          setStages((prevStages) => {
            const existingIds = new Set(prevStages.map((s) => s.id));
            const uniqueNew = newStages.filter((s) => !existingIds.has(s.id));
            return [...prevStages, ...uniqueNew];
          });
        }

        // Flatten leads from all stages
        const newLeads = Object.entries(data).flatMap(([stageId, stageLeads]) =>
          stageLeads.map((lead, index) => {
            const metaMap = Object.fromEntries(
              (lead.meta || []).map((m) => [m.meta_key, m.meta_value])
            );
            return {
              id: lead.leadid?.toString() || index.toString(),
              type: lead.type,
              userid: lead?.userid,
              name: lead.name,
              service: lead.service || "-",
              source: lead.leadsource || "",
              leadtype: lead.leadtype || "",
              current_stage: stageId,
              order: index,
              contact_name: lead.name || "",
              contact_email: lead.email || "",
              contact_phone: lead.phone || "",
              leadkey: lead.leadkey || lead?.leadid,
              assigned:
                Array.isArray(lead?.assigned_users) &&
                lead.assigned_users.length > 0
                  ? lead.assigned_users
                  : Array.isArray(lead?.assigned_user_details) &&
                    lead.assigned_user_details.length > 0
                  ? lead.assigned_user_details
                  : "",

              time: lead?.leadtime,
              country: lead?.country,
              state: lead?.state,
              message: lead?.message,
              completeLead: lead,
            };
          })
        );

        // console.log(newLeads);

        // Replace on page 1, append on next pages
        setLeads((prev) => (page === 1 ? newLeads : [...prev, ...newLeads]));

        setPagination(res.pagination || {}); // assuming API returns pagination
      }
    } catch (err) {
      console.error("Error loading leads:", err);
    } finally {
      setIsLoading(false);
      setIsApiPending(false);
    }
  };

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (!divRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = divRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 180) {
        if (
          !isApiPendingRef.current &&
          pagination?.current_page < pagination?.last_page
        ) {
          isApiPendingRef.current = true;
          setIsApiPending(true);
          setCardload(true);

          loadLeadsAndStages(Number(pagination?.current_page) + 1).finally(
            () => {
              isApiPendingRef.current = false;
              setIsApiPending(false);
              setCardload(false);
            }
          );
        }
      }
    };

    const divElement = divRef.current;
    divElement?.addEventListener("scroll", handleScroll);

    return () => divElement?.removeEventListener("scroll", handleScroll);
  }, [pagination]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setIsLeadModalOpen(true);
  };

  const boxColor = {
    all_leads: { color: "#9D5FEF" },
    active_deal_leads: { color: "#5FE0EF" },
    today_paying_leads: { color: "#98ADFF" },
    overdue_payment_leads: { color: "#60E442" },
    not_contacted_2_days: { color: "#E9E03F" },
    unengaged_leads: { color: "#335CFF" },
  };

  const filteredLeads = useMemo(() => leads, [leads]);
  const getLeadsByStage = (stageId) =>
    filteredLeads
      .filter((l) => l.current_stage === stageId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      {isLoading ? (
        <InnerLoader />
      ) : (
        <div className="py-6 px-[15px] rounded-[10px]  border-[#e1E4Ea]  bg-[#fff]  h-full w-full overflow-hidden">
          <div
            ref={divRef}
            className="flex flex-col pb-2  overflow-auto "
            style={{ height: "calc(100%)" }}
          >
            <div className="flex-1 ">
              <div className="flex gap-6 h-full ">
                <AnimatePresence>
                  {stages.map((stage, index) => (
                    <motion.div
                      key={stage.id}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-80 flex-shrink-0"
                    >
                      <LeadStageColumn
                        stage={stage}
                        counts={counts}
                        groupBy={groupBy}
                        leads={getLeadsByStage(stage.id)}
                        onLeadClick={handleLeadClick}
                        isLoading={isLoading}
                        colors={boxColor[stage.id]}
                        isLast={stages?.length - 1 === index}
                        country={country}
                        cardLoad={cardLoad}
                        ProductList={ProductList}
                        DealStages={DealStages}
                        PackageList={PackageList}
                        UsersList={UserList}
                        SubProductList={SubProductList}
                        fetchSubProducts={fetchSubProducts}
                        fetchPackages={fetchPackages}
                        BrandList={BrandList}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {isLeadModalOpen && selectedLead?.type === "lead" && (
              <LeadQuickView
                onClose={() => {
                  setIsLeadModalOpen(false);
                  setSelectedLead({});
                }}
                data={{ ...selectedLead, leadid: selectedLead?.id }}
                ProductList={ProductList}
                PackageList={PackageList}
                DealStages={DealStages}
                SubProductList={SubProductList}
                fetchSubProducts={fetchSubProducts}
                fetchPackages={fetchPackages}
                onError={onError}
                onSubmit={onSuccess}
              />
            )}

            {isLeadModalOpen && selectedLead?.type === "contact" && (
              <ContactQuickView
                onClose={() => {
                  setIsLeadModalOpen(false);
                  setSelectedLead({});
                }}
                data={{ ...selectedLead, userid: selectedLead?.userid }}
                ClientList={ClientList}
                UserList={UserList}
                onLoadMoreClients={onLoadMoreClients}
                hasMoreClients={hasMoreClients}
                isLoadingClients={isLoadingClients}
                ProductList={ProductList}
                PackageList={PackageList}
                DealStages={DealStages}
              />
            )}

            <Snackbar
              open={alertData.isShow}
              autoHideDuration={3000}
              onClose={handleCloseAlert}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Alert
                onClose={handleCloseAlert}
                severity={alertData.type}
                sx={{ width: "100%" }}
              >
                {alertData.message}
              </Alert>
            </Snackbar>
          </div>
        </div>
      )}
    </>
  );
}
